<script lang="ts">
  /* LanguageSwitcher — compact locale picker for the pages that render
     outside the AppShell (login, signup, onboarding). Setting `locale`
     persists to localStorage and, once authenticated, to the user profile
     (see lib/i18n.ts). */
  import { _ } from "svelte-i18n";
  import { LANGUAGES, locale, type Lang } from "$lib/i18n";

  let { align = "right" }: { align?: "left" | "right" } = $props();

  let open = $state(false);
  let wrap = $state<HTMLElement | null>(null);

  const current = $derived(LANGUAGES.find((l) => l.code === $locale) ?? LANGUAGES[0]);

  function pick(code: Lang) {
    locale.set(code);
    open = false;
  }

  function onWindowClick(e: MouseEvent) {
    if (open && wrap && !wrap.contains(e.target as Node)) open = false;
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") open = false;
  }
</script>

<svelte:window onclick={onWindowClick} onkeydown={onKey} />

<div class="ls" bind:this={wrap}>
  <button
    type="button"
    class="ls-btn"
    class:active={open}
    aria-haspopup="listbox"
    aria-expanded={open}
    aria-label={$_("common.language")}
    title={$_("common.language")}
    onclick={() => (open = !open)}
  >
    <span class="material-icons-outlined" aria-hidden="true">language</span>
    <span class="ls-short">{current.short}</span>
    <span class="material-icons-outlined ls-caret" aria-hidden="true">expand_more</span>
  </button>

  {#if open}
    <ul class="ls-menu" class:left={align === "left"} role="listbox" aria-label={$_("common.language")}>
      {#each LANGUAGES as lang}
        <li>
          <button
            type="button"
            role="option"
            aria-selected={lang.code === $locale}
            class:selected={lang.code === $locale}
            onclick={() => pick(lang.code)}
          >
            <span class="ls-code">{lang.short}</span>
            <span class="ls-label">{lang.label}</span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .ls { position: relative; display: inline-flex; }

  .ls-btn {
    display: inline-flex; align-items: center; gap: 4px;
    border: 1px solid var(--color-divider); background: #fff;
    border-radius: var(--radius); cursor: pointer;
    padding: 6px 8px 6px 10px;
    font-family: inherit; font-size: 13px; font-weight: 600;
    color: var(--color-text);
    transition: border-color 0.12s, color 0.12s;
  }
  .ls-btn:hover, .ls-btn.active { border-color: var(--color-primary); color: var(--color-primary); }
  .ls-btn .material-icons-outlined { font-size: 18px; }
  .ls-caret { font-size: 16px !important; opacity: 0.7; }
  .ls-short { letter-spacing: 0.02em; }

  .ls-menu {
    position: absolute; top: calc(100% + 6px); right: 0; z-index: 1200;
    margin: 0; padding: 4px; list-style: none;
    min-width: 178px; max-height: 320px; overflow-y: auto;
    background: #fff; border: 1px solid var(--color-divider);
    border-radius: var(--radius); box-shadow: 0 10px 30px rgba(0, 0, 0, 0.14);
  }
  .ls-menu.left { right: auto; left: 0; }
  .ls-menu button {
    display: flex; align-items: center; gap: 10px; width: 100%;
    border: none; background: none; cursor: pointer;
    padding: 8px 10px; border-radius: 4px;
    font-family: inherit; font-size: 13.5px; text-align: left;
    color: var(--color-text);
  }
  .ls-menu button:hover { background: var(--color-bg); }
  .ls-menu button.selected { color: var(--color-primary); font-weight: 700; }
  .ls-code {
    font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
    color: var(--color-text-muted); min-width: 22px;
  }
  .ls-menu button.selected .ls-code { color: var(--color-primary); }
  .ls-label { flex: 1; }
</style>
